import type { Plan, Subscription, Tenant } from "../models";
import { cloneSeedData, SEED_TENANTS } from "../data/seed";

const USE_API = import.meta.env.VITE_ITAAS_USE_TENANT_API === "true";
const PERSIST_KEY = "itaas:tenant-store:v1";
const SEED_TENANT_IDS = new Set(SEED_TENANTS.map((t) => t.id));

interface PersistedTenantSlice {
  tenants: Tenant[];
  subscriptions: Subscription[];
}

function loadPersistedSlice(): PersistedTenantSlice | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedTenantSlice;
    if (
      !Array.isArray(parsed.tenants) ||
      !Array.isArray(parsed.subscriptions)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function savePersistedSlice(data: ReturnType<typeof cloneSeedData>): void {
  if (typeof window === "undefined") return;
  const slice: PersistedTenantSlice = {
    tenants: data.tenants.filter((t) => !SEED_TENANT_IDS.has(t.id)),
    subscriptions: data.subscriptions.filter(
      (s) => !SEED_TENANT_IDS.has(s.tenantId)
    ),
  };
  window.localStorage.setItem(PERSIST_KEY, JSON.stringify(slice));
}

function hydrateStore(): ReturnType<typeof cloneSeedData> {
  const seed = cloneSeedData();
  const persisted = loadPersistedSlice();
  if (!persisted) return seed;

  const tenantIds = new Set(seed.tenants.map((t) => t.id));
  for (const tenant of persisted.tenants) {
    if (!tenantIds.has(tenant.id)) {
      seed.tenants.push(tenant);
      tenantIds.add(tenant.id);
    }
  }

  const subIds = new Set(seed.subscriptions.map((s) => s.id));
  for (const subscription of persisted.subscriptions) {
    if (!subIds.has(subscription.id)) {
      seed.subscriptions.push(subscription);
      subIds.add(subscription.id);
    }
  }

  return seed;
}

let store = hydrateStore();

export function resetTenantStore(): void {
  store = cloneSeedData();
  savePersistedSlice(store);
}

export function getStoreTenants(): Tenant[] {
  return store.tenants;
}

export function getStorePlans(): Plan[] {
  return store.plans;
}

export function getStoreSubscriptions(): Subscription[] {
  return store.subscriptions;
}

export function shouldUseTenantApi(): boolean {
  return USE_API;
}

export function mutateStore(
  mutator: (data: ReturnType<typeof cloneSeedData>) => void
): void {
  mutator(store);
  savePersistedSlice(store);
}
